import { useId, type ChangeEvent, type FC } from 'react';
import { typography } from '@mui/system';
import { useTranslation } from 'react-i18next';

import { callDetailsExtensionColumnWidth } from 'features/call/call-details/styles';
import { sanitizePhoneExtension } from 'features/call/call-hearing/helpers';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

export type CallDetailsExtensionInputWidth = 'full' | 'column' | 'sixty';
export type CallDetailsExtensionInputAppearance = 'light' | 'dark';

export interface CallDetailsExtensionInputProps {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  width?: CallDetailsExtensionInputWidth;
  appearance?: CallDetailsExtensionInputAppearance;
}

const extensionInputWidth = (width: CallDetailsExtensionInputWidth): string => {
  switch (width) {
    case 'full':
      return '100%';
    case 'sixty':
      return '60%';
    case 'column':
      return callDetailsExtensionColumnWidth;
    default: {
      const exhaustiveCheck: never = width;
      throw new Error(`Unhandled extension input width: ${exhaustiveCheck}`);
    }
  }
};

export const CallDetailsExtensionInput: FC<CallDetailsExtensionInputProps> = ({
  value,
  onChange,
  disabled = false,
  width = 'column',
  appearance = 'light',
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'callDetails' });
  const inputId = useId();
  const label = t(disabled ? 'extension' : 'enterExtension');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(sanitizePhoneExtension(event.target.value));
  };

  return (
    <Container $width={width} $appearance={appearance}>
      <StyledLabel htmlFor={inputId}>{label}</StyledLabel>
      <StyledInput
        $appearance={appearance}
        id={inputId}
        name='hearingPhoneExtension'
        data-testid='call-details-extension-input'
        inputMode='tel'
        autoComplete='off'
        disabled={disabled}
        value={value}
        onChange={handleChange}
      />
    </Container>
  );
};

const StyledLabel = styled('label')`
  color: ${theme.palette.grey[100]};
  display: inline-block;
  ${typography(theme.typography.label)};
`;

const Container = styled('div')<{
  $width: CallDetailsExtensionInputWidth;
  $appearance: CallDetailsExtensionInputAppearance;
}>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(3)};
  margin-bottom: ${({ $appearance }) =>
    theme.spacing($appearance === 'dark' ? 2 : 6)};
  width: ${({ $width }) => extensionInputWidth($width)};
  max-width: 100%;
  box-sizing: border-box;
  ${({ $width }) => ($width === 'full' ? '' : 'align-self: flex-start;')}
`;

const StyledInput = styled('input')<{
  $appearance: CallDetailsExtensionInputAppearance;
}>`
  ${typography(theme.typography.body1)};
  display: block;
  width: 100%;
  max-width: 100%;
  padding: ${theme.spacing(4, 6)};
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? theme.palette.grey[50]
      : theme.palette.common.black};
  background-color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.common.white};
  border: none;
  border-radius: ${theme.spacing(4)};
  outline: none;
  box-sizing: border-box;

  &:disabled {
    color: ${theme.palette.grey[50]};
    background-color: ${theme.palette.grey[800]};
    border: 1px solid ${theme.palette.grey[600]};
    opacity: 0.7;
  }
`;
